import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authClient, useSession } from "@/lib/auth-client";
import { adoptUnscopedHistory } from "@/lib/search-history";

export interface WorkspaceInfo {
  id: string;
  name: string;
  slug: string;
  role: string;
  logo: string | null;
  metadata?: string | null;
}

export interface WorkspaceContextValue {
  workspaces: WorkspaceInfo[];
  activeWorkspace: WorkspaceInfo | null;
  activeOrganizationId: string | null;
  isLoading: boolean;
  switchWorkspace: (organizationId: string | null) => Promise<void>;
  refetch: () => Promise<void>;
  currentRole: string;
  isOwner: boolean;
  isAdminOrOwner: boolean;
}

export const WorkspaceContext = createContext<WorkspaceContextValue>({
  workspaces: [],
  activeWorkspace: null,
  activeOrganizationId: null,
  isLoading: true,
  switchWorkspace: async () => {},
  refetch: async () => {},
  currentRole: "",
  isOwner: false,
  isAdminOrOwner: false,
});

export function useWorkspaceState(): WorkspaceContextValue {
  const { data: session, isPending: sessionLoading } = useSession();
  const userId = session?.user?.id ?? "";
  const [workspaces, setWorkspaces] = useState<WorkspaceInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentRole, setCurrentRole] = useState("");

  const sessionOrganizationId =
    ((session?.session as Record<string, unknown> | undefined)?.activeOrganizationId as
      | string
      | null
      | undefined) ?? null;

  // A session with no active workspace works in the Private one (the API falls back to it too).
  const activeWorkspace = useMemo(() => {
    if (workspaces.length === 0) return null;
    if (!sessionOrganizationId) return workspaces.find((w) => w.name === "Private") ?? null;
    return workspaces.find((w) => w.id === sessionOrganizationId) ?? null;
  }, [sessionOrganizationId, workspaces]);
  const activeOrganizationId = activeWorkspace?.id ?? sessionOrganizationId;

  const privateId = workspaces.find((w) => w.name === "Private")?.id;
  useEffect(() => {
    if (privateId) adoptUnscopedHistory(privateId);
  }, [privateId]);

  const isOwner = currentRole === "owner";
  const isAdminOrOwner = currentRole === "owner" || currentRole === "admin";

  const loadWorkspaces = useCallback(async () => {
    try {
      const result = await authClient.organization.list();
      if (result.error) return;
      const orgs = (result.data ?? []) as unknown as WorkspaceInfo[];

      const enriched = await Promise.all(
        orgs.map(async (org) => {
          try {
            const detailsResult = await authClient.organization.getFullOrganization({
              query: { organizationId: org.id },
            });
            if (!detailsResult.error && detailsResult.data) {
              const details = detailsResult.data as {
                members?: Array<{ userId: string; role: string }>;
              };
              const member = details.members?.find((m) => m.userId === userId);
              return { ...org, role: member?.role ?? "" };
            }
          } catch {
            // keep original role (likely empty)
          }
          return org;
        }),
      );

      setWorkspaces(enriched);
    } catch (err) {
      console.error(
        "[workspace] Failed to list workspaces:",
        err instanceof Error ? err.message : err,
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!sessionLoading && session) {
      loadWorkspaces();
    } else if (!sessionLoading && !session) {
      setLoading(false);
    }
  }, [sessionLoading, session, loadWorkspaces]);

  useEffect(() => {
    if (!activeOrganizationId) {
      setCurrentRole("");
      return;
    }
    const active = workspaces.find((w) => w.id === activeOrganizationId);
    setCurrentRole(active?.role ?? "");
  }, [activeOrganizationId, workspaces]);

  const switchWorkspace = useCallback(async (organizationId: string | null) => {
    try {
      await authClient.organization.setActive({
        organizationId: organizationId ?? (null as never),
      });
      window.location.reload();
    } catch (err) {
      console.error(
        "[workspace] Failed to switch workspace:",
        err instanceof Error ? err.message : err,
      );
    }
  }, []);

  return {
    workspaces,
    activeWorkspace,
    activeOrganizationId,
    isLoading: loading || sessionLoading,
    switchWorkspace,
    refetch: loadWorkspaces,
    currentRole,
    isOwner,
    isAdminOrOwner,
  };
}

export function useWorkspace(): WorkspaceContextValue {
  return useContext(WorkspaceContext);
}
